/**
 * Heure d'ouverture de la salle d'examen LILATE (modalité examinateur).
 *
 * Le candidat et l'examinateur n'entrent dans la salle qu'à partir de
 * EXAMINATION_ROOM_OPENING_MINUTES_BEFORE_START minutes avant le début prévu
 * de la session ; le staff y entre à tout moment.
 *
 * Le backend applique la même règle et reste la source de vérité
 * (ExaminationSession.is_before_connexion_opening, refus `session_not_open_yet`).
 *
 * L'heure courante est reçue en paramètre (`now`) : la salle passe l'heure du serveur
 * (ServerClockService), pas celle du poste, qui peut être décalée (SUP-2371).
 *
 * Les textes affichés pendant l'attente sont des clés de traduction
 * `examination.room-opening.*` (site examination), créées par
 * `api-recurring/recurring/scripts/seed_examination_room_opening_translations.py`.
 */

/** Aligné avec le backend (ExaminationSession.CONNEXION_OPENING_MINUTES_BEFORE_START). */
export const EXAMINATION_ROOM_OPENING_MINUTES_BEFORE_START = 10;

const ROLES_SUBJECT_TO_EXAMINATION_ROOM_OPENING = ['teacher', 'student', 'studentExternal'];

/** Paramètres des messages d'attente, à l'heure du navigateur. */
export interface ExaminationRoomOpeningLabels {
    /** Heure de début de l'examen (« 12h30 » en français, « 12:30 » sinon). */
    startTime: string;
    /** Heure d'ouverture de la salle, même format. */
    openingTime: string;
    /** Jour de l'examen (JJ/MM/AAAA) quand il n'a pas lieu aujourd'hui, sinon null. */
    startDay: string | null;
}

/** Clé de traduction et paramètres d'un libellé. */
export interface TranslatedLabel {
    key: string;
    params: { [name: string]: string | number };
}

/**
 * Heure d'ouverture de la salle : début prévu moins le délai d'ouverture,
 * ou null si le début n'est pas connu.
 */
export function getExaminationRoomOpeningDate(start: string | Date | null | undefined): Date | null {
    if (!start) {
        return null;
    }
    const startDate = new Date(start);
    if (isNaN(startDate.getTime())) {
        return null;
    }
    return new Date(startDate.getTime() - EXAMINATION_ROOM_OPENING_MINUTES_BEFORE_START * 60000);
}

/**
 * Millisecondes restant avant l'ouverture de la salle pour ce rôle : 0 si la salle
 * est déjà ouverte, si le rôle n'y est pas soumis (staff) ou si le début n'est pas connu.
 */
export function getMillisecondsUntilExaminationRoomOpening(
    start: string | Date | null | undefined,
    role: string,
    now: Date = new Date()
): number {
    if (ROLES_SUBJECT_TO_EXAMINATION_ROOM_OPENING.indexOf(role) === -1) {
        return 0;
    }
    const openingDate = getExaminationRoomOpeningDate(start);
    if (!openingDate) {
        return 0;
    }
    return Math.max(0, openingDate.getTime() - now.getTime());
}

/**
 * Heures de début et d'ouverture, et jour de l'examen s'il n'a pas lieu aujourd'hui.
 * L'heure s'écrit « 12h30 » en français et « 12:30 » dans les autres langues.
 */
export function getExaminationRoomOpeningLabels(
    start: string | Date,
    language: string | null,
    now: Date = new Date()
): ExaminationRoomOpeningLabels {
    const startDate = new Date(start);
    const openingDate = getExaminationRoomOpeningDate(startDate);
    const hourSeparator = language === 'fr' ? 'h' : ':';
    const startsToday = startDate.toDateString() === now.toDateString();
    return {
        startTime: formatHour(startDate, hourSeparator),
        openingTime: formatHour(openingDate, hourSeparator),
        startDay: startsToday ? null : formatDay(startDate),
    };
}

/** Clé du message d'attente : « Votre examen commence à … » ou « … commence le … à … ». */
export function getExaminationRoomOpeningMessageKey(labels: ExaminationRoomOpeningLabels): string {
    return labels.startDay
        ? 'examination.room-opening.message-other-day'
        : 'examination.room-opening.message-today';
}

/**
 * Temps restant avant l'ouverture, arrondi à la minute supérieure (au moins 1 min) :
 * « 47 min », ou « 1 h 05 min » à partir d'une heure.
 */
export function getExaminationRoomOpeningCountdown(millisecondsUntilOpening: number): TranslatedLabel {
    const totalMinutes = Math.max(1, Math.ceil(millisecondsUntilOpening / 60000));
    if (totalMinutes < 60) {
        return { key: 'examination.room-opening.countdown-minutes', params: { minutes: totalMinutes } };
    }
    return {
        key: 'examination.room-opening.countdown-hours',
        params: { hours: Math.floor(totalMinutes / 60), minutes: pad(totalMinutes % 60) },
    };
}

function formatHour(date: Date, separator: string): string {
    return `${pad(date.getHours())}${separator}${pad(date.getMinutes())}`;
}

function formatDay(date: Date): string {
    return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`;
}

function pad(value: number): string {
    return value < 10 ? `0${value}` : `${value}`;
}
